/**
 * Sidebar navigation config — single source of truth for nav items, their
 * icons, route paths, section grouping, and visibility (permission / role).
 * Also exposes a path -> page title lookup for the topbar.
 */
import React from 'react';
import {
  InsightsRounded,
  ViewAgendaRounded,
  GroupsRounded,
  EventBusyRounded,
  WorkspacePremiumRounded,
  SellRounded,
  ReceiptLongRounded,
  PersonRounded,
  StorefrontRounded,
} from '@mui/icons-material';
import { can, isAdmin, isAdministrator, PermissionKey } from '../auth/session';

export interface NavItem {
  label: string;
  path: string;
  icon: React.ReactElement;
  section: 'main' | 'billing' | 'account';
  /** Visibility predicate; defaults to visible. */
  visible?: () => boolean;
}

const perm = (key: PermissionKey) => () => can(key);

export const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: <InsightsRounded />, section: 'main', visible: isAdmin },
  { label: 'Queues', path: '/queues', icon: <ViewAgendaRounded />, section: 'main' },
  { label: 'Team', path: '/team', icon: <GroupsRounded />, section: 'main', visible: () => can('can_access_team_tab') || can('can_manage_team') },
  { label: 'Holidays', path: '/holidays', icon: <EventBusyRounded />, section: 'main', visible: perm('can_set_holidays') },

  { label: 'Subscriptions', path: '/subscriptions', icon: <WorkspacePremiumRounded />, section: 'billing', visible: isAdministrator },
  { label: 'Plans & Pricing', path: '/plans', icon: <SellRounded />, section: 'billing', visible: isAdministrator },
  { label: 'Payment History', path: '/payments', icon: <ReceiptLongRounded />, section: 'billing', visible: isAdministrator },

  { label: 'Profile & Settings', path: '/profile', icon: <PersonRounded />, section: 'account' },
  { label: 'Business Details', path: '/business', icon: <StorefrontRounded />, section: 'account', visible: isAdministrator },
];

export const SECTION_LABELS: Record<NavItem['section'], string | null> = {
  main: null,
  billing: 'Billing',
  account: 'Account',
};

/** Resolve the page title for a path (used by the topbar). Longest-prefix match. */
export function pageTitleFor(pathname: string): string {
  const titles: Array<[string, string]> = [
    ['/dashboard', 'Dashboard'],
    ['/revenue', 'Revenue'],
    ['/queues/new', 'New Queue'],
    ['/queues', 'Queues'],
    ['/team', 'Team'],
    ['/holidays', 'Holidays'],
    ['/subscriptions', 'Subscriptions'],
    ['/plans', 'Plans & Pricing'],
    ['/payments', 'Payment History'],
    ['/profile', 'Profile & Settings'],
    ['/business', 'Business Details'],
  ];
  const match = titles.find(([prefix]) => pathname === prefix || pathname.startsWith(prefix + '/'));
  return match ? match[1] : 'BaseQueue';
}
